// ---------------------------------------------------------------------------
// LanguageSelector.tsx — Dropdown component for switching UI language
// ---------------------------------------------------------------------------
// Renders a button showing the current locale flag + name, with a dropdown
// listing all supported locales. Uses SVG flag indicators per locale.
// ---------------------------------------------------------------------------

'use client';

import { useState, useRef, useCallback, useEffect, useId } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import {
  SUPPORTED_LOCALES,
  LOCALE_MAP,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from './i18n.config';
import { useTranslation } from './useTranslation';

// ---------------------------------------------------------------------------
// SVG Flag component — renders recognizable country flag per locale.
// Each flag uses correct national colors and distinctive layout.
// ---------------------------------------------------------------------------

function FlagIcon({ flagCode, className, uid }: { flagCode: string; className?: string; uid?: string }) {
  const clipId = uid ? `fc-${flagCode}-${uid}` : `fc-${flagCode}`;
  return (
    <svg
      viewBox="0 0 20 14"
      fill="none"
      className={cn('shrink-0', className)}
      aria-hidden="true"
    >
      <defs>
        <clipPath id={clipId}><rect width="20" height="14" rx="2" /></clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {flagCode === 'US' && (
          <>
            <rect width="20" height="14" fill="#FFF" />
            <rect y="0" width="20" height="2" fill="#B22234" />
            <rect y="4" width="20" height="2" fill="#B22234" />
            <rect y="8" width="20" height="2" fill="#B22234" />
            <rect y="12" width="20" height="2" fill="#B22234" />
            <rect width="9" height="8" fill="#3C3B6E" />
            <circle cx="2" cy="1.5" r="0.45" fill="#FFF" />
            <circle cx="4" cy="1.5" r="0.45" fill="#FFF" />
            <circle cx="6" cy="1.5" r="0.45" fill="#FFF" />
            <circle cx="8" cy="1.5" r="0.45" fill="#FFF" />
            <circle cx="3" cy="3" r="0.45" fill="#FFF" />
            <circle cx="5" cy="3" r="0.45" fill="#FFF" />
            <circle cx="7" cy="3" r="0.45" fill="#FFF" />
            <circle cx="2" cy="4.5" r="0.45" fill="#FFF" />
            <circle cx="4" cy="4.5" r="0.45" fill="#FFF" />
            <circle cx="6" cy="4.5" r="0.45" fill="#FFF" />
            <circle cx="8" cy="4.5" r="0.45" fill="#FFF" />
            <circle cx="3" cy="6" r="0.45" fill="#FFF" />
            <circle cx="5" cy="6" r="0.45" fill="#FFF" />
            <circle cx="7" cy="6" r="0.45" fill="#FFF" />
          </>
        )}
        {flagCode === 'SA' && (
          <>
            <rect width="20" height="14" fill="#006C35" />
            <rect x="3" y="5.5" width="14" height="0.8" rx="0.4" fill="#FFF" />
            <rect x="9" y="3" width="0.8" height="4" rx="0.4" fill="#FFF" />
            <rect x="6" y="8" width="8" height="0.6" rx="0.3" fill="#FFF" />
            <rect x="5" y="9" width="10" height="1.2" rx="0.3" fill="#FFF" opacity="0.4" />
          </>
        )}
        {flagCode === 'TH' && (
          <>
            <rect width="20" height="14" fill="#FFF" />
            <rect y="0" width="20" height="2.33" fill="#A51931" />
            <rect y="4.66" width="20" height="4.68" fill="#2D2A4A" />
            <rect y="11.67" width="20" height="2.33" fill="#A51931" />
          </>
        )}
        {flagCode === 'ES' && (
          <>
            <rect width="20" height="14" fill="#F1BF00" />
            <rect y="0" width="20" height="3.5" fill="#AA151B" />
            <rect y="10.5" width="20" height="3.5" fill="#AA151B" />
            <rect x="4" y="4.5" width="1.8" height="3" rx="0.3" fill="#AA151B" opacity="0.6" />
            <rect x="4.4" y="4" width="1" height="0.8" rx="0.3" fill="#AA151B" opacity="0.6" />
          </>
        )}
        {flagCode === 'FR' && (
          <>
            <rect x="0" width="6.67" height="14" fill="#002395" />
            <rect x="6.67" width="6.66" height="14" fill="#FFF" />
            <rect x="13.33" width="6.67" height="14" fill="#ED2939" />
          </>
        )}
        {flagCode === 'DE' && (
          <>
            <rect y="0" width="20" height="4.67" fill="#000" />
            <rect y="4.67" width="20" height="4.66" fill="#DD0000" />
            <rect y="9.33" width="20" height="4.67" fill="#FFCC00" />
          </>
        )}
        {flagCode === 'JP' && (
          <>
            <rect width="20" height="14" fill="#FFF" />
            <circle cx="10" cy="7" r="3.5" fill="#BC002D" />
          </>
        )}
        {flagCode === 'CN' && (
          <>
            <rect width="20" height="14" fill="#DE2910" />
            <polygon points="4,1.5 4.6,3.4 6.6,3.4 5,4.5 5.6,6.4 4,5.3 2.4,6.4 3,4.5 1.4,3.4 3.4,3.4" fill="#FFDE00" />
            <circle cx="8.5" cy="2" r="0.6" fill="#FFDE00" />
            <circle cx="9.8" cy="3.3" r="0.6" fill="#FFDE00" />
            <circle cx="9.8" cy="5.2" r="0.6" fill="#FFDE00" />
            <circle cx="8.5" cy="6.5" r="0.6" fill="#FFDE00" />
          </>
        )}
        {flagCode === 'IN' && (
          <>
            <rect width="20" height="14" fill="#FFF" />
            <rect y="0" width="20" height="4.67" fill="#FF9933" />
            <rect y="4.67" width="20" height="4.66" fill="#FFF" />
            <rect y="9.33" width="20" height="4.67" fill="#138808" />
            <circle cx="10" cy="7" r="1.6" fill="none" stroke="#000080" strokeWidth="0.5" />
            <circle cx="10" cy="7" r="0.4" fill="#000080" />
          </>
        )}
        {!['US','SA','TH','ES','FR','DE','JP','CN','IN'].includes(flagCode) && (
          <>
            <rect width="20" height="7" fill="#6B7280" />
            <rect y="7" width="20" height="7" fill="#9CA3AF" />
          </>
        )}
      </g>
      <rect x="0.5" y="0.5" width="19" height="13" rx="1.5" stroke="currentColor" strokeOpacity="0.08" fill="none" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// LanguageSelector
// ---------------------------------------------------------------------------

interface LanguageSelectorProps {
  id?: string;
  /** Compact mode: show only flag + chevron (no text) */
  compact?: boolean;
  /** Open dropdown upward instead of downward (useful when near bottom of screen) */
  dropUp?: boolean;
  className?: string;
}

/** Known public route prefixes (no leading slash) */
const PUBLIC_PREFIXES = [
  'features', 'pricing', 'about', 'contact', 'blog', 'case-studies', 'partner-program',
  'register-hotel', 'privacy', 'terms', 'refund-policy', 'security',
  'hotel-', 'hotelstack-vs-', 'integrations', 'compliance', 'demos',
  'best-hotel-management-software', 'hotels-in-',
  'security-whitepaper',
];

function isPublicPage(pathname: string): boolean {
  const stripped = pathname.replace(/^\//, '');
  // Root is also public
  if (stripped === '' || stripped === '/') return true;
  // Remove existing locale prefix if present (e.g. /ar/features → features)
  const parts = stripped.split('/');
  const segment = SUPPORTED_LOCALES.includes(parts[0] as SupportedLocale) ? parts[1] ?? '' : parts[0];
  // Locale home page (e.g., /ar, /es) — segment is empty after stripping locale
  if (segment === '') return true;
  return PUBLIC_PREFIXES.some((p) => segment.startsWith(p));
}

function getSlugFromPathname(pathname: string): string {
  const stripped = pathname.replace(/^\//, '');
  const parts = stripped.split('/');
  // If first part is a locale code, skip it
  if (SUPPORTED_LOCALES.includes(parts[0] as SupportedLocale)) {
    return parts.slice(1).join('/');
  }
  return stripped;
}

export function LanguageSelector({
  id = 'i18n-language-selector',
  compact = false,
  dropUp = false,
  className,
}: LanguageSelectorProps) {
  const { locale, setLocale } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();

  const instanceId = useId().replace(/:/g, '');

  const currentLocale = LOCALE_MAP[locale];

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleSelect = useCallback(
    (code: SupportedLocale, e: React.MouseEvent) => {
      // Prevent event from leaking to elements behind the dropdown
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);

      // On public pages, navigate to locale-prefixed URL for SEO.
      // Persist the locale to localStorage/cookie first so it survives
      // page transitions and is detectable by other components.
      if (isPublicPage(pathname)) {
        setLocale(code);
        const slug = getSlugFromPathname(pathname);
        if (code === DEFAULT_LOCALE) {
          router.push(slug ? `/${slug}` : '/');
        } else {
          router.push(slug ? `/${code}/${slug}` : `/${code}`);
        }
        return;
      }

      // Non-public pages (PMS, chain, platform): no URL-based locale, just
      // update the Zustand store so the UI re-renders in the new language.
      setLocale(code);
    },
    [setLocale, pathname, router],
  );

  return (
    <div
      ref={containerRef}
      className={cn('relative', className)}
    >
      {/* Trigger */}
      <button
        id={id}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-2 rounded-lg border border-hms-border px-3 py-2',
          'text-sm font-medium text-hms-textSub transition-all duration-200',
          'hover:border-hms-gold/40 hover:text-hms-text',
          'focus:outline-none focus:ring-2 focus:ring-hms-gold/30',
          open && 'border-hms-gold/40 text-hms-text',
        )}
        style={{ touchAction: 'manipulation' }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Select language"
      >
        <FlagIcon flagCode={currentLocale.flagCode} className="h-3.5 w-5" uid={instanceId} />
        {!compact && (
          <span className="hidden sm:inline">{currentLocale.nativeName}</span>
        )}
        {compact && (
          <Globe className="h-4 w-4 sm:hidden" />
        )}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {/* Click-trap overlay — closes dropdown on any outside click/tap */}
      {open && (
        <div
          id={`${id}-overlay`}
          className="fixed inset-0 z-[59]"
          aria-hidden="true"
          onClick={() => setOpen(false)}
          onPointerDown={(e) => { e.stopPropagation(); setOpen(false); }}
        />
      )}

      {/* Dropdown */}
      {open && (
        <ul
          id={`${id}-dropdown`}
          role="listbox"
          aria-label="Available languages"
          className={cn(
            'absolute right-0 z-[60] w-56',
            dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            'rounded-xl border border-hms-border bg-hms-card',
            'shadow-xl shadow-black/30 backdrop-blur-xl',
            'overflow-hidden py-1',
            'max-h-[70vh] overflow-y-auto',
          )}
          style={{ touchAction: 'manipulation' }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {SUPPORTED_LOCALES.map((code) => {
            const loc = LOCALE_MAP[code];
            const isActive = code === locale;
            return (
              <li key={code} role="option" aria-selected={isActive}>
                <button
                  id={`${id}-option-${code}`}
                  type="button"
                  onClick={(e) => handleSelect(code, e)}
                  style={{ touchAction: 'manipulation' }}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'bg-hms-gold/10 text-hms-gold'
                      : 'text-hms-textSub hover:bg-white/[0.04] hover:text-hms-text',
                  )}
                >
                  <FlagIcon flagCode={loc.flagCode} className="h-3.5 w-5" uid={instanceId} />
                  <span className="flex-1 text-left font-medium">
                    {loc.nativeName}
                  </span>
                  <span className="text-xs text-hms-textDim">
                    {loc.englishName}
                  </span>
                  {isActive && (
                    <Check className="h-4 w-4 shrink-0 text-hms-gold" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
